/**
 * Sessie 55 blok 2: de leveranciersadressen in Beheer.
 *
 * Instelling `leverancier_adressen` (dict merk -> e-mailadres). De
 * leveranciermelding bij een kwaliteitsklacht pakt hier het adres van
 * het merk; staat het merk er niet in, dan vult JP het adres op de kaart
 * in. Elke toevoeging en elke verwijdering schrijft direct weg (les van
 * 8 september): er is geen concept in de browser dat los kan lopen.
 */
import { useState, type CSSProperties, type FormEvent } from "react";
import { toast } from "sonner";

export const LEVERANCIER_ADRESSEN = "leverancier_adressen";

/** Eenvoudige, strenge vorm: iets@iets.tld, geen spaties. */
const EMAIL_VORM = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isEmailadres(tekst: string): boolean {
  return EMAIL_VORM.test(tekst.trim());
}

/** Alleen geldige paren (tekst -> tekst); de rest van een vreemde waarde valt weg. */
export function alsAdressen(waarde: unknown): Record<string, string> {
  if (!waarde || typeof waarde !== "object" || Array.isArray(waarde)) return {};
  const uit: Record<string, string> = {};
  for (const [merk, adres] of Object.entries(waarde as Record<string, unknown>)) {
    if (typeof adres === "string") uit[merk] = adres;
  }
  return uit;
}

const VELD: CSSProperties = {
  padding: "6px 8px",
  border: "1px solid var(--wp-lijn)",
  borderRadius: 7,
  background: "var(--wp-bg)",
  color: "var(--wp-tekst)",
  /* 16px op mobiel voorkomt dat iOS bij focus inzoomt. */
  fontSize: 16,
  minWidth: 0,
};

export function LeverancierAdressen({
  waarde,
  bezig,
  onZet,
}: {
  /** `undefined`: de sleutel bestaat (nog) niet in de instellingen. */
  waarde: unknown;
  bezig: boolean;
  onZet: (waarde: Record<string, string>) => void;
}) {
  const [merk, setMerk] = useState("");
  const [adres, setAdres] = useState("");
  const bestaat = waarde !== undefined;
  const adressen = alsAdressen(waarde);
  const merken = Object.keys(adressen).sort((a, b) =>
    a.localeCompare(b, "nl", { sensitivity: "base" }),
  );

  function voegToe(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    const schoonMerk = merk.trim();
    const schoonAdres = adres.trim();
    if (!schoonMerk) {
      toast.error("Vul een merk in.");
      return;
    }
    if (!isEmailadres(schoonAdres)) {
      toast.error("Vul een geldig e-mailadres in, zoals kwaliteit@merk.nl.");
      return;
    }
    const dubbel = merken.find(
      (bekend) => bekend.toLowerCase() === schoonMerk.toLowerCase(),
    );
    if (dubbel) {
      toast.error(`${dubbel} staat er al. Verwijder dat adres eerst.`);
      return;
    }
    onZet({ ...adressen, [schoonMerk]: schoonAdres });
    setMerk("");
    setAdres("");
  }

  function verwijder(weg: string) {
    const rest = { ...adressen };
    delete rest[weg];
    onZet(rest);
  }

  return (
    <div
      data-testid="wp-beheer-leveranciers"
      style={{ padding: "12px 14px", borderBottom: "1px solid var(--wp-lijn2)" }}
    >
      <div style={{ fontWeight: 600 }}>Leveranciersadressen</div>
      <div style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>
        Per merk het adres voor een kwaliteitsmelding (product, batch, THT,
        foto's en ordernummer, nooit klantgegevens). Staat het merk er niet
        in, dan kan de melding niet weg: zet het adres hier en vink de kaart
        daarna opnieuw aan. Versturen gebeurt altijd pas na jouw vinkje.
      </div>
      {!bestaat ? (
        <div style={{ fontSize: 12, color: "var(--wp-rood)", marginTop: 4 }}>
          Deze instelling bestaat nog niet op de server; zodra hij er is, kun
          je hier adressen toevoegen.
        </div>
      ) : (
        <>
          {merken.length === 0 ? (
            <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--wp-tekst2)" }}>
              Nog geen adressen.
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
              {merken.map((naam) => (
                <li
                  key={naam}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "baseline",
                    padding: "4px 0",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 500, minWidth: 140 }}>{naam}</span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      color: "var(--wp-tekst2)",
                    }}
                  >
                    {adressen[naam]}
                  </span>
                  <button
                    type="button"
                    className="wp-knop"
                    disabled={bezig}
                    aria-label={`Verwijder ${naam}`}
                    onClick={() => verwijder(naam)}
                  >
                    Verwijderen
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form
            onSubmit={voegToe}
            style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}
          >
            <input
              value={merk}
              onChange={(e) => setMerk(e.target.value)}
              aria-label="Merk"
              placeholder="Merk"
              style={{ ...VELD, width: 160 }}
            />
            <input
              value={adres}
              onChange={(e) => setAdres(e.target.value)}
              aria-label="E-mailadres leverancier"
              placeholder="kwaliteit@merk.nl"
              inputMode="email"
              type="text"
              style={{ ...VELD, width: "min(280px, 60vw)" }}
            />
            <button type="submit" className="wp-knop" disabled={bezig}>
              Toevoegen
            </button>
          </form>
        </>
      )}
    </div>
  );
}
